import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';

import {EThreadContext, MessagingService} from '@library/nxt/_services/messaging.service';

import {ClientService} from '@library/shared/_services/client.service';
import {UserService} from '@library/nxt/_services/user.service';
import {PageService} from '@library/shared/_services/page.service';
import {IFirestoreQuery} from '@library/nxt/_services/fire.service';
import {PopButtonComponent} from '@library/shared/buttons/pop-button.component';
import {IMenuItem, Thread, ThreadMessage, User, Client} from '@nxt/model-core';
import {OnDestroyPage} from '@library/shared/_inherited/ondestroy.page';
import {MessageItemSummary} from './message-item-summary';
import {ThreadItemComponent} from './thread-item';
import {ScrollableGenericList} from '@library/nxt/list/scrollable-generic-list';
import {Router} from '@angular/router';
import {IconsComponent} from '@library/shared/icons/icons.component';
import {PipesModule} from '@library/shared/_pipes/pipes';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'unread-list',
    standalone: true,
    imports: [
        CommonModule, IconsComponent, PopButtonComponent,
        ScrollableGenericList, MessageItemSummary, ThreadItemComponent, PipesModule, IconsComponent
    ],
    template: `
        <div class="border-b border-gray-200">
            <div class="w-full flex justify-between place-items-center pt-4">
                <div class="flex pl-3"></div>
                <div class="flex">
                    <button class="btn-clear -mt-4" *ngIf="showDeleteBtn" (click)="removeSelected()">
                        <icon name="heroicon-outline-trash" class="h-5 w-5 text-red-600"></icon>
                    </button>
                    <ng-container *ngIf="clientMenu?.length">
                        <pop-button iconName="heroicon-solid-dots-vertical"
                                    iconClass="h-4 w-4 text-gray-500 ml-1 mb-1"
                                    [items]="clientMenu"
                                    label="Other Unreads"
                                    btnClass="border-transparent inline-flex mr-1 text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm"
                        ></pop-button>
                    </ng-container>
                    <pop-button iconName="heroicon-solid-dots-vertical"
                                iconClass="h-4 w-4 text-gray-500 ml-1 mb-1"
                                [items]="actionsMenu"
                                label="Bulk Actions"
                                btnClass="border-transparent inline-flex mr-1 text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm"
                    ></pop-button>
                </div>
            </div>
        </div>

        <div class="pb-14">
            <scrollable-generic-list
                    #msgList
                    [baseQuery]="baseQuery"
                    label="Unreads"
                    [pageSize]="20"
                    [itemTemplate]="itemTemplate"
                    [path]="path"
                    [watch]="false"
                    [autoStart]="true"
                    (onItemsLoaded)="buildActionMenu()"
            ></scrollable-generic-list>
        </div>

        <ng-template let-item="item" let-items="items" let-i="i" #itemTemplate>
            <div class="flex w-full border-b border-gray-200" *ngIf="item?.id">
                <div class="flex place-items-start">
                    <button (click)="toggle(item)"
                            class="btn-sm btn-clear w-8 h-8 p-0"
                    >
                        <icon [name]="((selected.all && !excepted[item.id]) || selected[item.id]) ? 'heroicon-outline-check-circle' : 'heroicon-outline-x'" class="h-6 w-6 text-gray-600 hover:text-gray-400"></icon>
                    </button>
                </div>
                <div class="w-full h-full cursor-pointer hover:bg-gray-100">
                    <message-item-summary
                            *ngIf="item['_type']==='threadsmsgs'"
                            (onSelect)="mSvc.showThread(item)"
                            [unread]="item.unread || item._unread?.seen === false"
                            [message]="item"
                            [context]="EThreadContext.UNREAD"
                    ></message-item-summary>
                    <thread-item
                            *ngIf="item['_type']==='threads'"
                            [thread]="item"
                            [loadAll]="true"
                            [context]="EThreadContext.UNREAD"
                            (onSelect)="mSvc.showThread(item)"
                    ></thread-item>
                </div>
            </div>
        </ng-template>
    `
})
export class UnreadListComponent extends OnDestroyPage implements OnChanges {
    @ViewChild('msgList', { read: ScrollableGenericList }) msgList: ScrollableGenericList;
    @Input() roles: string[] = ['sales'];
    @Input() user: User;
    @Input() client: Client;
    @Input() clients: Client[];
    @Input() counts: any;
    @Input() show: boolean;

    activeItem: Thread | ThreadMessage;
    height: string;
    EThreadContext = EThreadContext;
    tabs: IMenuItem[] = [];
    uBR: any;
    baseQuery: IFirestoreQuery[] = [
        { name: 'orderBy', args: ['date','desc'] }
    ];
    path: string;
    clientMenu: IMenuItem[];
    selected: any = {};
    excepted: any = {};

    get showDeleteBtn() : boolean {
        return !!Object.keys(this.selected||{}).length;
    }
    actionsMenu: IMenuItem[] = [];

    constructor(
        public cSvc: ClientService,
        public uSvc: UserService,
        public mSvc: MessagingService,
        public pSvc: PageService,
        private router: Router
    ) {
        super();
        this.mSvc.counts$
            .pipe(takeUntil(this.d$))
            .subscribe(counts => {
                if (this.counts?.total !== counts?.total || this.counts?.unread !== counts?.unread) {
                    this.msgList?.loadData();
                }
                this.counts = counts;
            })
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.user?.id && this.client?.id && this.show) {
            this.setPath();
            this.loadClients();
            this.buildActionMenu();
        }
    }

    setPath() {
        this.path = `users/${this.user.id}/clients/${this.cSvc.client_id}/unread` ;
    }

    buildActionMenu() {
        if (this.showDeleteBtn) {

            this.actionsMenu = [
                {
                    label: 'Unselect All',
                    click: async () => {
                        this.unselectAll();
                    }
                },
                {
                    label: 'Mark Selected as Read',
                    click: async () => {
                        const idsSelected = Object.keys(this.selected);
                        const idsExcepted = Object.keys(this.excepted);
                        await this.mSvc.updateUnreads(
                            this.user,
                            this.client,
                            { idsSelected, idsExcepted, action:'seen' }
                        );
                        this.unselectAll();
                    }
                },
                {
                    label: 'Mark Selected as Unread',
                    click: async () => {
                        const idsSelected = Object.keys(this.selected);
                        const idsExcepted = Object.keys(this.excepted);
                        await this.mSvc.updateUnreads(
                            this.user,
                            this.client,
                            { idsSelected, idsExcepted, action:'unseen' }
                        );
                        this.unselectAll();
                    }
                },
                {
                    label: 'Delete Selected',
                    click: async () => {
                        return this.removeSelected();
                    }
                }
            ];

        } else {

            const loadedCount = this.msgList?.items?.length||0;

            this.actionsMenu = [
                {
                    label: `Select All Visible (${loadedCount})`,
                    click: async () => {
                        this.selected = {};
                        if (this.msgList?.items?.length) {
                            this.msgList.items.forEach(item => {
                                this.selected[item.id] = true;
                            });
                        }
                        this.buildActionMenu();
                    }
                }
            ];

            if (loadedCount && !this.msgList.doneLoading) {
                this.actionsMenu.push({
                    label: 'Select ALL',
                    click: async () => {
                        this.selectAll();
                    }
                });
            }

            this.actionsMenu.push({
                    label: 'Remove Old Unreads',
                    click: async () => {

                        this.pSvc.notification$.next({
                            title: 'Delete Old Unreads',
                            timeout: 0,
                            message: `Delete all messages older than: `,
                            buttons: [
                                {
                                    label: 'Cancel'
                                },
                                {
                                    label: '7 Days',
                                    click: () => {
                                        this.removeOldUnreads(7);
                                    }
                                },
                                {
                                    label: '14 Days',
                                    click: () => {
                                        this.removeOldUnreads(14);
                                    }
                                },
                                {
                                    label: '30 Days',
                                    click: () => {
                                        this.removeOldUnreads(30);
                                    }
                                }
                            ]
                        });

                    }
                });
        }
    }

    async loadClients() {
        if (
            this.counts
            && this.client?.id
            && this.clients?.length
        ) {
            this.clientMenu = Object.keys(this.counts).reduce((items,id) => {
                if (id !== this.client.id) {
                    let c = this.clients.find(c => c.id === id);
                    if (c && this.counts[c.id].total) {
                        items.push({
                            label: `${c.name} (${this.counts[id].total})`,
                            click: async () => {
                                await this.router.navigate([`/${c.name_key}/threads/inbox/unread`]);
                            }
                        });
                    }
                }
                return items;
            }, []);
        } else {
            this.clientMenu = [];
        }
    }

    async toggle(item: Thread | ThreadMessage) {
        if (this.selected.all) {
            this.excepted[item?.id] = !this.excepted[item?.id];
            if (!this.excepted[item.id]) {
                delete this.excepted[item?.id];
            }
        } else {
            this.selected[item?.id] = !this.selected[item?.id];
            if (!this.selected[item.id]) {
                delete this.selected[item?.id];
            }
        }
        this.buildActionMenu();
    }

    selectAll() {
        this.selected = { all: true };
        this.excepted = {};
        this.buildActionMenu();
    }

    unselectAll() {
        this.selected = {};
        this.excepted = {};
        this.buildActionMenu();
    }

    removeOldUnreads(days: number, confirm?: boolean) {
        if (confirm) {
            this.removeSelected(days);
        } else {
            this.pSvc.notification$.next({
                title: 'Are You Sure?',
                timeout: 0,
                message: `This will delete all unreads older than ${days} days.`,
                buttons: [
                    {
                        label: 'Cancel'
                    },
                    {
                        label: 'Confirm Delete',
                        click: () => {
                            this.unselectAll();
                            this.removeOldUnreads(days, true);
                        }
                    }
                ]
            })
        }
    }

    async removeSelected(days?: number) {
        this.pSvc.loading$.next(true);
        const idsSelected = Object.keys(this.selected);
        const idsExcepted = Object.keys(this.excepted);
        await this.mSvc.updateUnreads(
            this.user,
            this.client,
            { idsSelected, idsExcepted, action:'delete', days }
        );
        this.unselectAll();
        this.pSvc.loading$.next(false);
        let max: number = 10000;
        let wait: number = (idsSelected.includes('all')) ? max : idsSelected.length * 100;
        setTimeout(async () => {
            await this.msgList.loadData(true);
        }, wait < 10000 ? wait : max);
    }

}
